import React, { useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import { Box, Inline, Link } from '@99designs/emu';
import { maxWidths } from '@99designs/emu/settings';
import { IconLeft1 } from '@99designs/icons';
import { useOnboarder } from '../OnboarderContext';
import { LogoLink } from './LogoLink';
import type { Role } from '@99designs/site-navigation/consts';

interface Props {
    userRole: Role;
}

function getBackLinkText(backLinkUrl: string) {
    const { pathname } = new URL(backLinkUrl);

    const defaultText = __('Back to 99designs');

    if (RegExp('^/web-design').test(pathname)) {
        return __('Back to web design');
    }

    if (RegExp('^/blog/').test(pathname)) {
        return __('Back to 99designs blog');
    }

    return defaultText;
}

function valididateReferrer(referrer: string): string {
    try {
        const { hostname: referrerHostname } = new URL(referrer);
        const { hostname: currentHostname } = window.location;
        if (referrerHostname !== currentHostname) {
            return '';
        }
    } catch (error) {
        return '';
    }
    return referrer;
}

function setSessionStorageItem(name: string, value: string) {
    try {
        sessionStorage.setItem(name, value);
    } catch (error) {
        Bugsnag.notify(error, (event) => {
            event.context = `Failed to set session storage item for: ${name}`;
        });
    }
}

export function HeaderNav({ userRole }: Props) {
    const {
        config: { storageKeyPrefix },
    } = useOnboarder();
    const onboarderReferrerStorageKeyName = `${storageKeyPrefix}_referrer`;
    const [backLinkUrl, setBackLinkUrl] = useState<string>();

    useEffect(() => {
        const onboarderReferrer = valididateReferrer(document.referrer);
        const onboarderReferrerSessionItem = sessionStorage.getItem(
            onboarderReferrerStorageKeyName
        );

        if (!onboarderReferrer && onboarderReferrerSessionItem) {
            setBackLinkUrl(onboarderReferrerSessionItem);
        } else {
            setBackLinkUrl(onboarderReferrer);
        }

        if (
            !onboarderReferrerSessionItem ||
            (onboarderReferrer && onboarderReferrerSessionItem !== onboarderReferrer)
        ) {
            setSessionStorageItem(onboarderReferrerStorageKeyName, onboarderReferrer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            alignSelf="center"
            width="100%"
            display="flex"
            maxWidth={`${maxWidths.content.widescreen}px`}
            padding="1.5bu"
            role="banner"
        >
            <LogoLink userRole={userRole} />
            {backLinkUrl && (
                <Box marginLeft="auto">
                    <Link href={backLinkUrl} title={__('Go back')} color="grey700" size="size2">
                        <Inline space="0.25bu">
                            <IconLeft1 />
                            {getBackLinkText(backLinkUrl)}
                        </Inline>
                    </Link>
                </Box>
            )}
        </Box>
    );
}
